// EVE-Context-Builder. Pure functions. 1:1 portiert aus V1 useBuildContext.js.
// Konsumiert: marketStats + fullData + (optional) liveMetrics + tradesPromptBlock.
// Liefert: JSON-Block für EVE-System-Prompt + Breaking-News-Text-Block.

import type { FullData, LiveMetricsByAsset, MarketStats } from '@/types/market';

export interface BuildContextInput {
  marketStats: MarketStats | null;
  fullData: FullData | null;
  liveMetrics?: LiveMetricsByAsset;
  /** Vorformatierter Trade-Block (aus useTradingLog.formatForPrompt). `null` = keine Trades. */
  tradesPromptBlock?: string | null;
  /** Optional fixierte „Jetzt"-Zeit für Tests. */
  now?: Date;
}

interface PolymarketSnapshot {
  question: string;
  odds: string | number | unknown[] | null | undefined;
  volume24hr: string;
}

interface NormalizedNewsArticle {
  title?: string;
  source?: string;
  publishedAt?: string;
}

const isoOrNow = (now?: Date): string => (now ?? new Date()).toISOString();

const isWeekendDay = (d: Date): boolean => [0, 6].includes(d.getDay());

function normalizePolymarket(items: FullData['polymarket'] | undefined | null): PolymarketSnapshot[] | undefined {
  if (!items) return undefined;
  return items.slice(0, 5).map((m): PolymarketSnapshot => {
    const raw = m.outcomePrices;
    let odds: PolymarketSnapshot['odds'] = raw ?? null;
    try {
      const parsed = typeof raw === 'string' ? (JSON.parse(raw) as unknown) : raw;
      if (Array.isArray(parsed) && parsed.length >= 2) {
        odds = `Yes: ${Math.round(Number(parsed[0]) * 100)}% / No: ${Math.round(Number(parsed[1]) * 100)}%`;
      }
    } catch {
      // ignore — keep raw odds
    }
    return {
      question: m.question,
      odds,
      volume24hr: `${Math.round(((m.volume24hr ?? 0) as number) / 1000)}k`,
    };
  });
}

function pickArticles(
  block: { articles?: Array<{ title?: string; source?: { name?: string } | null; publishedAt?: string }> } | null | undefined,
  limit: number,
): NormalizedNewsArticle[] | undefined {
  if (!block?.articles) return undefined;
  return block.articles.slice(0, limit).map((a) => ({
    title: a.title,
    source: a.source?.name ?? undefined,
    publishedAt: a.publishedAt,
  }));
}

function pickReutersByCategory(items: FullData['reuters'] | null | undefined):
  | { crypto: string[]; macro: string[]; geo: string[] }
  | null {
  if (!items || items.length === 0) return null;
  const buckets: Record<'crypto' | 'macro' | 'geo', string[]> = { crypto: [], macro: [], geo: [] };
  for (const r of items) {
    if (r.category in buckets) buckets[r.category].push(r.title);
  }
  return {
    crypto: buckets.crypto.slice(0, 4),
    macro: buckets.macro.slice(0, 4),
    geo: buckets.geo.slice(0, 4),
  };
}

function buildJapanBlock(j: FullData['japan'] | undefined | null): Record<string, unknown> | null {
  if (!j) return null;
  const spread = j.bondSpread;
  const carryRisk =
    spread === null
      ? 'UNKNOWN'
      : spread > 3.5
        ? 'HOCH — Unwind-Risiko bei BOJ-Hike'
        : spread > 2.5
          ? 'MITTEL — Beobachten'
          : 'NIEDRIG';
  return {
    USDJPY: j.usdjpy
      ? { value: j.usdjpy.value, date: j.usdjpy.date, change: j.usdjpy.change }
      : null,
    Japan_10Y_Yield: j.jp10y ? { value: `${j.jp10y.value}%`, date: j.jp10y.date } : null,
    CarrySpread_US_minus_JP: spread !== null ? `${spread}%` : null,
    CarryTradeRisk: carryRisk,
    China_US_Treasury_Holdings_Mrd: j.chinaTreasury
      ? {
          value: j.chinaTreasury.value,
          date: j.chinaTreasury.date,
          change: j.chinaTreasury.change != null ? `${j.chinaTreasury.change}%` : null,
        }
      : null,
    BOJ_Meetings:
      j.bojMeetings?.map((m) => ({
        date: m.date,
        label: m.label,
        hikeProb:
          m.hikeProb > 0
            ? `${m.hikeProb.toFixed(1)}% Hike-Wahrscheinlichkeit`
            : 'keine Änderung erwartet',
        impliedRate: `${m.impliedRate}%`,
      })) ?? null,
    BOJ_AsOf: j.bojAsOf ?? null,
    BOJ_CurrentRate: j.bojCurrentRate != null ? `${j.bojCurrentRate}%` : null,
  };
}

function buildCotBlock(cot: FullData['cot'] | null | undefined): Record<string, unknown> | null {
  if (!cot || cot.length === 0) return null;
  const latest = cot[0]!;
  const prev = cot[1] ?? null;
  const netSignal =
    latest.net > 0
      ? `NET LONG +${latest.net.toLocaleString()} Kontrakte`
      : `NET SHORT ${latest.net.toLocaleString()} Kontrakte`;
  const trendDir = prev ? (latest.net > prev.net ? '↑ LONG-DRUCK STEIGT' : '↓ LONG-DRUCK FÄLLT') : null;

  let flipWeek: { von: string | null; auf: string | null; netVorher: number; netNachher: number } | null = null;
  for (let i = 0; i < cot.length - 1; i++) {
    const curr = cot[i]!;
    const nxt = cot[i + 1]!;
    if (curr.net >= 0 && nxt.net < 0) {
      flipWeek = { von: nxt.date, auf: curr.date, netVorher: nxt.net, netNachher: curr.net };
      break;
    }
  }

  return {
    aktuell: {
      datum: latest.date,
      long: latest.long,
      short: latest.short,
      net: latest.net,
      signal: netSignal,
      trend: trendDir,
      chg_long: latest.chgLong,
      chg_short: latest.chgShort,
      open_interest: latest.openInterest,
    },
    flip_signal: flipWeek
      ? `Non-Commercials wechselten von NET SHORT (${flipWeek.netVorher.toLocaleString()}) am ${flipWeek.von} auf NET LONG (+${flipWeek.netNachher.toLocaleString()}) am ${flipWeek.auf} — klassisches Bullish-Reversal-Signal`
      : null,
    history_16w: cot.map((w) => ({ datum: w.date, net: w.net, long: w.long, short: w.short })),
  };
}

/**
 * Baut den großen JSON-Kontext-Block für EVE-System-Prompts. V1-Verhalten 1:1.
 * `liveMetrics` ist optional — wenn nicht gesetzt, fallen wir auf `fullData.funding/oi`.
 */
export function buildEveContext(input: BuildContextInput): string {
  const { marketStats, fullData, liveMetrics = {}, tradesPromptBlock, now } = input;
  const m = fullData?.macro ?? {};
  const fng7 = fullData?.fng?.data?.slice(0, 7).map((d) => ({
    value: d.value,
    label: d.value_classification,
    date: d.timestamp,
  }));

  const payload = {
    crypto: {
      BTC: { price: marketStats?.btc?.price, change24h: marketStats?.btc?.change },
      ETH: { price: marketStats?.eth?.price, change24h: marketStats?.eth?.change },
      SOL: { price: marketStats?.sol?.price, change24h: marketStats?.sol?.change },
      globalMarketCap: fullData?.global?.data?.total_market_cap?.usd,
      btcDominance: fullData?.global?.data?.market_cap_percentage?.btc,
      totalVolume24h: fullData?.global?.data?.total_volume?.usd,
    },
    macro: {
      DXY: { price: m.dxy?.price, change24h: m.dxy?.change },
      VIX: { price: m.vix?.price, change24h: m.vix?.change },
      SP500: { price: m.spx?.price, change24h: m.spx?.change },
      Gold: { price: m.gold?.price, change24h: m.gold?.change },
      Oil_WTI: { price: m.oil?.price, change24h: m.oil?.change },
      OVX: { price: m.ovx?.price, change24h: m.ovx?.change },
      TLT: { price: m.tlt?.price, change24h: m.tlt?.change },
      Silver: { price: m.silver?.price, change24h: m.silver?.change },
      USDJPY: { price: m.jpy?.price, change24h: m.jpy?.change },
    },
    leverage: {
      BTC: {
        fundingRate: liveMetrics.BTC?.funding ?? fullData?.funding.fundingRate,
        openInterest: liveMetrics.BTC?.openInterest ?? fullData?.oi.openInterest,
        oraclePremium: liveMetrics.BTC?.premium,
      },
      ETH: {
        fundingRate: liveMetrics.ETH?.funding ?? fullData?.ethFunding.fundingRate,
        openInterest: liveMetrics.ETH?.openInterest ?? fullData?.ethOi.openInterest,
        oraclePremium: liveMetrics.ETH?.premium,
      },
      SOL: {
        fundingRate: liveMetrics.SOL?.funding ?? fullData?.solFunding.fundingRate,
        openInterest: liveMetrics.SOL?.openInterest,
        oraclePremium: liveMetrics.SOL?.premium,
      },
      GOLD: {
        fundingRate: liveMetrics.GOLD?.funding,
        openInterest: liveMetrics.GOLD?.openInterest,
        oraclePremium: liveMetrics.GOLD?.premium,
      },
      SILVER: {
        fundingRate: liveMetrics.SILVER?.funding,
        openInterest: liveMetrics.SILVER?.openInterest,
        oraclePremium: liveMetrics.SILVER?.premium,
      },
      OIL: {
        fundingRate: liveMetrics.CL?.funding,
        openInterest: liveMetrics.CL?.openInterest,
        oraclePremium: liveMetrics.CL?.premium,
      },
    },
    sentiment: {
      fearGreedIndex: marketStats?.fng?.value,
      fearGreedLabel: marketStats?.fng?.label,
      history7d: fng7,
    },
    polymarket: normalizePolymarket(fullData?.polymarket),
    trending: fullData?.trending?.coins?.slice(0, 7).map((c) => ({
      name: c.item?.name,
      symbol: c.item?.symbol,
      rank: c.item?.market_cap_rank,
    })),
    lunarCrush: {
      topCoins: fullData?.lunarList?.data?.slice(0, 5).map((c) => ({
        name: c.name,
        symbol: c.symbol,
        galaxyScore: c.galaxy_score,
        altRank: c.alt_rank,
      })),
      news: fullData?.lunarNews?.data?.slice(0, 5).map((n) => ({ title: n.title ?? n.name })),
    },
    cryptoNews: pickArticles(fullData?.newsApi, 8),
    geopoliticalNews: pickArticles(fullData?.newsApiGeo, 10),
    financialJuiceNews: (fullData?.fjHeadlines ?? []).slice(0, 12).map((n) => n.title ?? n),
    reuters: pickReutersByCategory(fullData?.reuters),
    japan_macro: buildJapanBlock(fullData?.japan),
    cot_bitcoin_cme: buildCotBlock(fullData?.cot),
    event_scores: fullData?.eventScores?.events?.length
      ? {
          scoredAt: fullData.eventScores.scoredAt,
          top_events: fullData.eventScores.events.map((e) => ({
            score: e.score,
            category: e.category,
            label: e.label,
            headline: e.headline,
          })),
        }
      : null,
    offene_trades: tradesPromptBlock ?? 'Keine offenen Trades.',
    isWeekend: isWeekendDay(now ?? new Date()),
    timestamp: isoOrNow(now),
  };

  return JSON.stringify(payload, null, 2);
}

/**
 * V1 `fetchBreakingNewsContext` — kompakter Text-Block für News-fokussierte Agents.
 */
export function buildBreakingNewsContext(input: {
  marketStats: MarketStats | null;
  fullData: FullData | null;
  now?: Date;
}): string {
  const { marketStats, fullData, now = new Date() } = input;
  const isWeekend = isWeekendDay(now);
  const nextMarketOpen = isWeekend
    ? `Märkte geschlossen. Nächste Öffnung: Montag ${now.getDay() === 6 ? 'übermorgen' : 'morgen'} ~09:30 ET / 15:30 CET`
    : 'Märkte offen';
  const fjHeadlines = fullData?.fjHeadlines ?? [];
  const cryptoNews = fullData?.newsApi?.articles ?? [];

  const num = (v: number | string | null | undefined, fallback = 0): number => {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : fallback;
    }
    return fallback;
  };

  return `
📊 MARKT-STATUS: ${nextMarketOpen}
🕐 ZEIT: ${now.toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })} CET
📅 WOCHENENDE: ${isWeekend ? 'Ja' : 'Nein'}

💰 MARKT-SNAPSHOT:
BTC: $${num(marketStats?.btc?.price).toLocaleString()} (${marketStats?.btc?.change ?? '?'}%)
ETH: $${num(marketStats?.eth?.price).toLocaleString()} (${marketStats?.eth?.change ?? '?'}%)
SOL: $${num(marketStats?.sol?.price).toLocaleString()} (${marketStats?.sol?.change ?? '?'}%)
VIX: ${marketStats?.vix?.price ?? '?'} (${marketStats?.vix?.change ?? '?'}%)
DXY: ${marketStats?.dxy?.price ?? '?'} (${marketStats?.dxy?.change ?? '?'}%)
Gold: $${num(marketStats?.gold?.price).toLocaleString()} (${marketStats?.gold?.change ?? '?'}%)
Fear & Greed: ${marketStats?.fng?.value ?? '?'} (${marketStats?.fng?.label ?? '?'})

📈 FINANCIAL JUICE (MAKRO/GEO LIVE):
${fjHeadlines.slice(0, 8).map((h) => `• ${h.title ?? ''}`).join('\n') || 'Keine FinancialJuice News verfügbar'}

🪙 CRYPTO NEWS (CoinTelegraph/CoinDesk):
${cryptoNews.slice(0, 6).map((a) => `• [${a.source?.name ?? 'Crypto'}] ${a.title ?? ''}`).join('\n') || 'Keine Crypto News verfügbar'}

HINWEIS: Du hast KEIN Live-Such-Tool. Nutze ausschließlich die oben gelieferten News + Marktdaten; erfinde nichts dazu.
`.trim();
}
